import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronRight, Plus, ShieldCheck, EyeOff, VolumeX, Users2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;import { SocialLinks } from '../components/Footer';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cca8665b-5293-4dd5-89e0-2957c9d7e36c/artifacts/zjtkx4ss_LOGO-MAYAPLUS.png";

const FAQ = [
  { q: "Maya+, qu'est-ce que c'est ?", a: "Maya+ est votre plateforme de streaming dédiée aux histoires qui ont du sens : films, séries, documentaires, programmes jeunesse, jeux interactifs et créations originales exclusives. Un catalogue accessible en illimité, à votre rythme, jour et nuit." },
  { q: "Combien coûte Maya+ ?", a: "Un seul tarif mensuel, connu d'avance, qui couvre tous vos appareils : téléphone, tablette, Smart TV et ordinateur. Plusieurs formules existent selon le nombre d'écrans dont vous avez besoin. Aucun coût caché, aucun engagement : vous arrêtez quand vous le souhaitez." },
  { q: "Où puis-je regarder Maya+ ?", a: "Partout où vous avez une connexion ! Depuis votre navigateur sur ordinateur, via l'application dédiée sur votre Smart TV, ou avec l'application mobile sur téléphone et tablette. Un seul compte suffit pour tout retrouver." },
  { q: "Comment puis-je annuler mon offre ?", a: "En toute liberté. Il n'y a ni durée minimale ni paperasse : la résiliation se fait directement en ligne, en quelques secondes, depuis votre espace compte. C'est gratuit, et vous pouvez revenir quand l'envie vous prend." },
  { q: "Que puis-je regarder sur Maya+ ?", a: "Un catalogue riche et soigneusement sélectionné : longs-métrages, séries, documentaires inspirants, dessins animés et programmes jeunesse, sans oublier les jeux et les créations originales Maya+ introuvables ailleurs. Le tout en illimité." },
  { q: "Est-ce que Maya+ est adapté aux enfants ?", a: "Absolument ! Chaque abonnement comprend un espace Jeunesse pensé pour eux : des profils enfants sécurisés par contrôle parental, des contenus triés selon l'âge, et la tranquillité d'esprit pour les parents." },
  { q: "Que sont les filtres de scènes ?", a: "C'est une exclusivité Maya+, très rare sur les plateformes de streaming : la possibilité de masquer automatiquement certains types de scènes (scènes intimes, nudité, violence, vulgarité…) pendant la lecture. Vous profitez du film ou de la série sans être exposé aux passages que vous préférez éviter — idéal pour regarder en famille ou rester fidèle à vos valeurs." },
  { q: "Comment fonctionnent les filtres de scènes ?", a: "Nos équipes identifient à l'avance les passages sensibles de chaque contenu. Depuis le lecteur, vous activez les filtres de votre choix en un clic : les scènes visuelles concernées sont alors sautées automatiquement (la lecture reprend juste après, sans manipulation), et les passages vulgaires à l'audio sont coupés le temps de la réplique. Chaque profil garde ses propres réglages : vos préférences sont mémorisées d'un film à l'autre, et même les miniatures de la barre de lecture ne dévoilent pas les scènes filtrées." },
];

const CDN = 'https://d3e29banmk53vx.cloudfront.net/landing';

const REASONS = [
  { img: `${CDN}/icon-tv.png`, title: "Maya+ sur grand écran", text: "Installez-vous confortablement : l'application Smart TV offre une navigation fluide et immersive, pensée pour le salon." },
  { img: `${CDN}/icon-screens.png`, title: "Plusieurs écrans à la fois", text: "Chacun regarde son programme selon votre formule, et chaque profil reprend sa lecture exactement où il s'était arrêté." },
  { img: `${CDN}/icon-globe.png`, title: "Emportez Maya+ partout", text: "Votre catalogue vous suit sur TV, téléphone, tablette et ordinateur, à la maison comme en déplacement." },
  { img: `${CDN}/icon-kids.png`, title: "Un espace rien que pour les enfants", text: "Profils protégés par contrôle parental et programmes adaptés à leur âge : les plus jeunes explorent en toute sécurité, sans supplément." },
];

function EmailCta({ prefix = 'hero' }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.preventDefault();
    const em = email.toLowerCase().trim();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/lookup`, { email: em });
      navigate(data.exists ? `/verify-code?email=${encodeURIComponent(em)}` : `/signup?email=${encodeURIComponent(em)}`);
    } catch {
      navigate(`/signup?email=${encodeURIComponent(em)}`);
    }
  };
  return (
    <form onSubmit={go} className="flex flex-col sm:flex-row items-stretch gap-3 w-full max-w-2xl mx-auto" data-testid={`${prefix}-email-form`}>
      <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Adresse e-mail" required
        data-testid={`${prefix}-email-input`}
        className="flex-1 bg-black/50 border border-white/25 rounded-md px-5 py-4 text-white text-base placeholder-white/50 focus:outline-none focus:border-[#E9641A] backdrop-blur-sm transition-colors" />
      <button type="submit" disabled={busy} data-testid={`${prefix}-start-btn`}
        className="flex items-center justify-center gap-2 bg-[#E9641A] hover:bg-[#FE975E] disabled:opacity-70 text-white text-xl font-bold px-8 py-4 rounded-md transition-colors">
        Commencer <ChevronRight size={24} />
      </button>
    </form>
  );
}

function FiltersShowcase() {
  const filters = [
    { label: 'Scènes intimes', action: 'Passer' },
    { label: 'Nudité', action: 'Passer' },
    { label: 'Violence', action: 'Passer' },
    { label: 'Vulgarité audio', action: 'Couper le son' },
  ];
  return (
    <section className="px-6 md:px-14 pt-14" data-testid="landing-filters">
      <div className="max-w-6xl mx-auto rounded-2xl border border-white/10 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1d0f33 0%, #170a29 55%, #2a1447 100%)' }}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center p-7 md:p-10">
          {/* Texte */}
          <div>
            <p className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[#E9641A] bg-[#E9641A]/10 border border-[#E9641A]/25 rounded-full px-3 py-1.5 mb-4">
              <ShieldCheck size={13} /> Exclusivité Maya+
            </p>
            <h2 className="text-lg md:text-lg font-bold mb-3">Les filtres de scènes : regardez selon vos valeurs</h2>
            <p className="text-sm md:text-base text-white/60 leading-relaxed mb-5">
              Une fonctionnalité inédite, que très peu de plateformes proposent : choisissez ce que vous ne voulez pas voir,
              Maya+ s'occupe du reste. Les scènes concernées sont automatiquement sautées pendant la lecture — sans manipulation, sans spoiler, sans y être exposé.
            </p>
            <ul className="space-y-2.5 text-sm text-white/75">
              <li className="flex items-start gap-2.5"><EyeOff size={16} className="text-[#E9641A] flex-none mt-0.5" /> Scènes intimes, nudité ou violence : sautées automatiquement, la lecture reprend juste après.</li>
              <li className="flex items-start gap-2.5"><VolumeX size={16} className="text-[#E9641A] flex-none mt-0.5" /> Vulgarité à l'audio : le son est coupé le temps de la réplique.</li>
              <li className="flex items-start gap-2.5"><Users2 size={16} className="text-[#E9641A] flex-none mt-0.5" /> Chaque profil garde ses propres réglages, mémorisés d'un contenu à l'autre.</li>
            </ul>
          </div>
          {/* Mockup panneau de filtres */}
          <div className="relative" aria-hidden="true">
            <div className="rounded-2xl border border-white/10 bg-black/50 backdrop-blur-sm p-5 max-w-sm mx-auto shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
              <p className="flex items-center gap-2 text-sm font-bold mb-4"><ShieldCheck size={16} className="text-[#E9641A]" /> Filtres de scènes</p>
              <div className="space-y-2.5">
                {filters.map(f => (
                  <div key={f.label} className="flex items-center justify-between bg-white/5 rounded-lg px-3.5 py-2.5">
                    <div>
                      <p className="text-sm font-semibold">{f.label}</p>
                      <p className="text-[11px] text-white/45">{f.action}</p>
                    </div>
                    <span className="relative w-10 h-[22px] rounded-full bg-[#E9641A] flex-none">
                      <span className="absolute top-[3px] left-[22px] w-4 h-4 rounded-full bg-white" />
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <span className="absolute -top-3 -right-1 md:right-6 bg-[#E9641A] text-white text-[11px] font-bold px-3 py-1.5 rounded-full shadow-lg rotate-3">En 1 clic</span>
          </div>
        </div>
        {/* Mockup timeline : comment ça marche sur un film */}
        <div className="px-7 md:px-10 pb-8" aria-hidden="true">
          <div className="rounded-xl border border-white/10 bg-black/60 p-5">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <p className="text-xs font-bold text-white/60 uppercase tracking-wider">Pendant la lecture d'un film</p>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-white/80 bg-white/10 rounded-full px-3 py-1">
                <ShieldCheck size={12} className="text-[#E9641A]" /> Scène filtrée — passée automatiquement
              </span>
            </div>
            <div className="relative pt-6">
              <span className="absolute left-[46%] -translate-x-1/2 top-0 inline-flex items-center gap-1 text-[10px] font-bold text-[#E9641A]"><EyeOff size={11} /> Scène ignorée</span>
              <div className="h-1.5 rounded-full bg-white/15 relative overflow-visible">
                <div className="absolute inset-y-0 left-0 w-[38%] bg-[#E9641A] rounded-l-full" />
                <div className="absolute inset-y-[-3px] left-[38%] w-[16%] rounded"
                  style={{ background: 'repeating-linear-gradient(45deg, rgba(233,100,26,0.55) 0 5px, rgba(255,255,255,0.12) 5px 10px)', border: '1px solid rgba(233,100,26,0.6)' }} />
                <div className="absolute inset-y-0 left-[54%] w-[6%] bg-[#E9641A]" />
                <span className="absolute top-1/2 -translate-y-1/2 left-[60%] w-3.5 h-3.5 rounded-full bg-white shadow-[0_0_10px_rgba(233,100,26,0.8)]" />
              </div>
              <div className="flex justify-between text-[10px] text-white/35 mt-2">
                <span>0:00</span><span>La lecture saute la scène et reprend ici ↑</span><span>1:48:00</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [posters, setPosters] = useState([]);
  const [top10, setTop10] = useState([]);
  const [minPrice, setMinPrice] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    axios.get(`${API}/contents`).then(r => {
      const list = (r.data || []).filter(c => c.poster_url || c.thumbnail_url);
      setPosters(list.slice(0, 48));
      const originals = list.filter(c => (c.categories || []).includes('originals'));
      setTop10(originals.slice(0, 10));
    }).catch(() => {});
    axios.get(`${API}/plans`).then(r => {
      const prices = (r.data || []).map(p => p.price).filter(p => p > 0);
      if (prices.length) setMinPrice(Math.min(...prices));
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-black text-white" style={{ fontFamily: "'Manrope', 'Avenir', Arial, sans-serif" }} data-testid="landing-page">
      {/* ─── HERO ─── */}
      <div className="relative overflow-hidden">
        {/* Poster mosaic background */}
        <div className="absolute inset-0" aria-hidden="true">
          <div className="absolute -inset-x-[10%] -top-[12%] -bottom-[12%]" style={{ transform: 'rotate(-4deg) scale(1.12)' }}>
            <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
              {posters.map((c, i) => (
                <div key={c.id || i} className="rounded-md overflow-hidden bg-[#1a0e2e] aspect-[2/3]">
                  <img src={c.poster_url || c.thumbnail_url} alt="" loading="lazy" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.75) 100%)' }} />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black to-transparent" />
        </div>

        {/* Header */}
        <header className="relative z-10 flex items-center justify-between px-6 md:px-14 pt-5">
          <img src={LOGO_URL} alt="Maya+" className="h-10 md:h-14" data-testid="landing-logo" />
          <button onClick={() => navigate('/auth')} data-testid="landing-signin-btn"
            className="bg-[#E9641A] hover:bg-[#FE975E] text-white text-sm font-semibold px-5 py-2 rounded-md transition-colors">
            S'identifier
          </button>
        </header>

        {/* Hero content */}
        <div className="relative z-10 flex flex-col items-center text-center px-6 pt-24 pb-32 md:pt-36 md:pb-44 max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-5" style={{ lineHeight: 1.5 }} data-testid="landing-title">
            Des films, des séries, des jeux : tout Maya+ en illimité et en <span className="landing-secure-glow">sécurité</span>
          </h1>
          <p className="text-lg md:text-xl font-semibold mb-8">
            {minPrice != null ? `Dès ${String(minPrice).replace('.', ',')} € par mois. Sans engagement, résiliable quand vous voulez.` : 'Sans engagement, résiliable quand vous voulez.'}
          </p>
          <p className="text-base md:text-lg text-white/90 mb-4 max-w-2xl">
            Envie de découvrir Maya+ ? Indiquez votre adresse e-mail pour créer votre compte ou retrouver votre abonnement.
          </p>
          <EmailCta prefix="hero" />
        </div>

        {/* Curved separator */}
        <div className="relative z-10 -mb-1">
          <div className="h-2 w-full" style={{ background: 'linear-gradient(90deg, transparent, #E9641A 30%, #75D4D5 70%, transparent)', borderRadius: '50% 50% 0 0 / 100% 100% 0 0', boxShadow: '0 -8px 40px rgba(233,100,26,0.45)' }} />
        </div>
      </div>

      {/* ─── OFFER BANNER ─── */}
      <section className="px-6 md:px-14 pt-12">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center gap-5 rounded-2xl border border-white/10 px-6 py-5"
          style={{ background: 'linear-gradient(90deg, #1d0f33 0%, #2a1447 55%, #3b1a55 100%)' }} data-testid="landing-offer-banner">
          <span className="w-16 h-16 flex items-center justify-center flex-none">
            <img src={`${CDN}/icon-popcorn.png`} alt="" className="w-16 h-16 object-contain drop-shadow-[0_4px_14px_rgba(233,100,26,0.35)]" />
          </span>
          <div className="flex-1 text-center sm:text-left">
            <p className="font-bold text-lg md:text-xl">Toute l'expérience Maya+{minPrice != null ? <> à partir de <span className="text-[#E9641A] font-extrabold text-2xl md:text-3xl">{String(minPrice).replace('.', ',')} €</span> seulement</> : ''}.</p>
            <p className="text-sm md:text-base text-white/60">Notre formule la plus accessible, toujours sans engagement.</p>
          </div>
          <button onClick={() => navigate('/auth?signup=1')} data-testid="landing-offer-btn"
            className="bg-white/10 hover:bg-white/20 border border-white/10 text-white text-sm font-semibold px-5 py-2.5 rounded-md transition-colors flex-none">
            En savoir plus
          </button>
        </div>
      </section>

      {/* ─── TENDANCES ─── */}
      {top10.length > 0 && (
        <section className="px-6 md:px-14 pt-14" data-testid="landing-trending">
          <h2 className="text-lg md:text-2xl font-bold mb-5 max-w-6xl mx-auto">Le top du moment sur Maya+</h2>
          <div className="max-w-6xl mx-auto flex gap-2 overflow-x-auto pb-4" style={{ scrollbarWidth: 'none' }}>
            {top10.map((c, i) => (
              <div key={c.id} className="relative flex-none flex items-end cursor-pointer group" onClick={() => navigate('/auth?signup=1')} data-testid={`landing-top10-${i + 1}`}>
                <span className="font-black text-[#1f1f1f] select-none" style={{
                  fontSize: '9rem', lineHeight: '0.75', marginRight: '-2.2rem', zIndex: 1,
                  WebkitTextStroke: '3px rgba(255,255,255,0.3)',
                  WebkitMaskImage: 'linear-gradient(to right, rgba(0,0,0,1) 35%, rgba(0,0,0,0) 100%)',
                  maskImage: 'linear-gradient(to right, rgba(0,0,0,1) 35%, rgba(0,0,0,0) 100%)',
                }}>{i + 1}</span>
                <div className="relative w-[130px] md:w-[150px] aspect-[2/3] rounded-lg overflow-hidden z-10 border border-white/10 group-hover:scale-105 transition-transform duration-300">
                  <img src={c.poster_url || c.thumbnail_url} alt={c.title} loading="lazy" className="w-full h-full object-cover" />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ─── FILTRES DE SCÈNES ─── */}
      <FiltersShowcase />

      {/* ─── RAISONS ─── */}
      <section className="px-6 md:px-14 pt-14" data-testid="landing-reasons">
        <h2 className="text-lg md:text-2xl font-bold mb-5 max-w-6xl mx-auto">Pourquoi choisir Maya+ ?</h2>
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {REASONS.map((r, i) => (
            <div key={i} className="relative rounded-2xl p-6 pb-20 border border-white/5 overflow-hidden"
              style={{ background: 'linear-gradient(150deg, #1d0f33 0%, #170a29 60%, #241040 100%)' }}>
              <h3 className="font-bold mb-3 text-base md:text-lg leading-snug">{r.title}</h3>
              <p className="text-sm text-white/55 leading-relaxed">{r.text}</p>
              <img src={r.img} alt="" loading="lazy" className="absolute bottom-4 right-4 w-20 h-20 md:w-24 md:h-24 object-contain drop-shadow-[0_6px_18px_rgba(233,100,26,0.35)]" />
            </div>
          ))}
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="px-6 md:px-14 pt-16" data-testid="landing-faq">
        <h2 className="text-lg md:text-2xl font-bold mb-5 max-w-6xl mx-auto">Vos questions, nos réponses</h2>
        <div className="max-w-6xl mx-auto space-y-2">
          {FAQ.map((f, i) => (
            <div key={i}>
              <button onClick={() => setOpenFaq(openFaq === i ? null : i)} data-testid={`landing-faq-${i}`}
                className="w-full flex items-center justify-between bg-[#1d1d1d] hover:bg-[#2a2a2a] transition-colors px-6 py-5 text-left">
                <span className="text-base md:text-xl font-medium">{f.q}</span>
                <Plus size={26} className={`flex-none transition-transform duration-300 ${openFaq === i ? 'rotate-45' : ''}`} />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-[500px]' : 'max-h-0'}`}>
                <div className="bg-[#1d1d1d] px-6 py-5 mt-px text-sm md:text-lg text-white/85 leading-relaxed">{f.a}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="max-w-2xl mx-auto text-center mt-12">
          <p className="text-base md:text-lg mb-4">Envie de découvrir Maya+ ? Indiquez votre adresse e-mail pour créer votre compte ou retrouver votre abonnement.</p>
          <EmailCta prefix="faq" />
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="px-6 md:px-14 pt-20 pb-12" data-testid="landing-footer">
        <div className="max-w-6xl mx-auto text-white/55 text-sm">
          <p className="mb-8">Une question ? Écrivez-nous à <a href="mailto:contact@mayaplus.fr" className="underline hover:text-white">contact@mayaplus.fr</a></p>
          <SocialLinks className="mb-8" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-3 gap-x-6 text-[13px] mb-10">
            <button onClick={() => navigate('/faq')} className="text-left underline hover:text-white" data-testid="landing-footer-faq">FAQ</button>
            <button onClick={() => navigate('/auth')} className="text-left underline hover:text-white">Compte</button>
            <button onClick={() => navigate('/cgu')} className="text-left underline hover:text-white" data-testid="landing-footer-cgu">Conditions d'utilisation</button>
            <button onClick={() => navigate('/confidentialite')} className="text-left underline hover:text-white" data-testid="landing-footer-privacy">Confidentialité</button>
            <a href="mailto:contact@mayaplus.fr" className="underline hover:text-white">Centre d'aide</a>
            <button onClick={() => navigate('/mentions-legales')} className="text-left underline hover:text-white" data-testid="landing-footer-legal">Mentions légales</button>
            <a href="mailto:contact@mayaplus.fr" className="underline hover:text-white">Nous contacter</a>
            <button onClick={() => navigate('/confidentialite')} className="text-left underline hover:text-white">Préférences de cookies</button>
          </div>
          <img src={LOGO_URL} alt="Maya+" className="h-7 mb-3 opacity-70" />
          <p className="text-xs text-white/35">Maya+ France — www.mayaplus.fr</p>
        </div>
      </footer>
    </div>
  );
}
